import { CSSProperties, memo, ReactNode, useCallback } from 'react';
import { TimeFilterType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { StringRecord } from 'libs/widgets/charts/src/types';
import {
  AxisGroup,
  AxisOption,
  BarChartAppearance,
  BarClick,
  Chart,
  ChartEditPanel,
  CustomLegendsData,
  FormatValue,
  XAxisLabelValueTick,
} from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text, Truncated } from '@frontend/design-system';
import { useTimeBasedBarChart, BarChartDataResponse, SeriesConfig } from '../hooks/use-time-based-bar-chart';
import { X_AXIS_TIME_FILTER_OPTIONS } from '../utils/chart-options-utils';
import { transformGroupName } from '../utils/chart-tooltip-utils';
import { formatStartDateWithTimeZone, formatEndDateWithTimeZone } from '../utils/days-and-dates';
import { AnalyticsInfoPopover } from './info-popover';

// ── Bar click callback params ──

export type BarClickParams = BarClick & {
  /** Pre-computed start date with timezone from the clicked group. */
  startDate: string;
  /** Pre-computed end date with timezone from the clicked group. */
  endDate: string;
};

// ── Component props ──

export type TimeBasedBarChartProps = {
  /** Response data from any analytics API. Structurally compatible with AirDataResponse. */
  data: BarChartDataResponse | undefined;
  /** Whether data is currently loading. */
  isLoading: boolean;
  /** Hint text to display as subtitle (used as default subtitle if `subtitle` is not provided). */
  filterHintText?: string;

  /** Defines how series are extracted from the data — single-series or stacked. */
  seriesConfig: SeriesConfig;
  /** Color map keyed by series key. */
  colors: StringRecord;
  /** Color map keyed by series key. */
  strokeColors?: StringRecord;
  /** Label map keyed by series key. */
  labels: StringRecord;

  /** Chart header configuration. Omit (or leave `title` empty) to render the chart without an internal title/info-tip. */
  header?: {
    /** Chart title text. */
    title?: string;
    /** Description text for the info-tip popover next to the title. */
    infoTipDescription?: string;
    /** Custom subtitle node. If not provided, defaults to `filterHintText`. */
    subtitle?: ReactNode;
  };

  /** X-axis configuration (consumer-controlled). */
  xAxis: {
    /** Current selected time filter. */
    selected: TimeFilterType;
    /** Default/initial x-axis selection (used by ChartEditPanel for reset). */
    defaultSelected: TimeFilterType;
    /** Called when the user changes the x-axis selection. */
    onChange: (xAxis: TimeFilterType) => void;
    /** Available x-axis options for the edit panel. Defaults to Daily / Day of Week / Hour of Day. */
    options?: AxisOption[];
  };

  /** Y-axis filter configuration (consumer-controlled, optional). */
  yAxis?: {
    /** Current selected y-axis filters. */
    selected: Record<string, string[]>;
    /** Default/initial y-axis selection (used by ChartEditPanel for reset). */
    defaultSelected: Record<string, string[]>;
    /** Called when the user changes the y-axis selection. */
    onChange: (yAxes: Record<string, string[]>) => void;
    /** Y-axis filter groups for the edit panel. */
    groups: AxisGroup[];
  };

  /**
   * Called when a bar is clicked. Only fires when x-axis is DAILY.
   * Receives pre-computed `startDate` and `endDate`.
   */
  onBarClick?: (params: BarClickParams) => void;

  /** Bar chart appearance overrides (e.g. `mode: 'stacked'`, `barRadius: 4`). Supports all BarChartAppearance properties. */
  appearance?: Partial<BarChartAppearance>;

  /** When true, show per-group total in tooltip header. */
  showTotal?: boolean;
  /** Chart height in pixels. Defaults to 240. */
  chartHeight?: number;

  /** Custom CSS styles for the chart container. Defaults to `{ marginTop: theme.spacing(3) }`. */
  style?: CSSProperties;

  /**
   * When true (stacked mode only), forces the stack/render order to exactly match
   * `seriesConfig.seriesKeys` regardless of per-bucket zero values. The last key in
   * the array renders on top of the stack. Defaults to false (order inferred from data).
   */
  preserveSeriesOrder?: boolean;

  /** Legends configuration. */
  legends?: {
    /** Custom legend data to override default labels. */
    customData?: Record<string, { label: string }>;
    /** When true, always show all legends without collapsing. */
    doNotCollapseLegends?: boolean;
    /** Custom value formatter for legend values. Defaults to `formatters.value`. */
    formatValue?: FormatValue;
    /** When true, allows clicking on custom legends to toggle visibility. Only applies when customData is provided. */
    allowClickOnCustomLegends?: boolean;
  };
};

/**
 * A reusable, fully-featured time-based bar chart component.
 *
 * Handles data transformation, tooltips, x-axis ticks, click behavior,
 * and the complete Chart JSX tree (Header, BarChart, Legends).
 *
 * Data is passed in externally — no API coupling.
 *
 * @example
 * ```tsx
 * <TimeBasedBarChart
 *   data={apiResponse}
 *   isLoading={isLoading}
 *   seriesConfig={{ mode: 'single', seriesKey: 'Answered' }}
 *   colors={{ Answered: theme.colors.primary50 }}
 *   labels={{ Answered: t('Answered') }}
 *   header={{
 *     title: t('AI Answered Calls'),
 *     infoTipDescription: t('Total calls answered by AI'),
 *   }}
 *   xAxis={{
 *     selected: selectedXAxis,
 *     defaultSelected: defaultXAxisSelected,
 *     onChange: setSelectedXAxis,
 *   }}
 *   onBarClick={({ startDate, endDate }) => { ... }}
 *   legends={{ formatValue: formatters.value }}
 * />
 * ```
 */
export const TimeBasedBarChart = memo(
  ({
    data,
    isLoading,
    filterHintText,
    seriesConfig,
    colors,
    strokeColors,
    labels,
    header,
    xAxis,
    yAxis,
    onBarClick,
    appearance,
    showTotal,
    chartHeight = 240,
    style,
    preserveSeriesOrder,
    legends,
  }: TimeBasedBarChartProps) => {
    const { t } = useTranslation('analytics');

    const { chartData, tooltipTitlesByName, tooltipHeadersByName, seriesTotals } = useTimeBasedBarChart({
      data,
      selectedXAxis: xAxis.selected,
      seriesConfig,
      showTotal,
    });

    // ── Click handler (only fires for DAILY x-axis) ──

    const handleBarClick = useCallback(
      ({ group, barId, value }: BarClick) => {
        if (!onBarClick) return;
        const startDate = formatStartDateWithTimeZone(group);
        const endDate = formatEndDateWithTimeZone(group);
        onBarClick({ barId, group, value, startDate, endDate });
      },
      [onBarClick]
    );

    // ── Axis change handlers ──

    const handleXChange = useCallback(
      (value: string) => {
        xAxis.onChange(value as TimeFilterType);
      },
      [xAxis.onChange]
    );

    const handleYChange = useCallback(
      (yAxes: Record<string, string[]>) => {
        yAxis?.onChange(yAxes);
      },
      [yAxis?.onChange]
    );

    // ── Build bar chart data ──

    const barChartData =
      seriesConfig.mode === 'stacked'
        ? { groups: chartData, stackKeys: seriesConfig.seriesKeys }
        : { groups: chartData };

    // When preserveSeriesOrder is enabled for stacked charts, pin the legend/stack
    // order to seriesKeys so it is deterministic regardless of per-bucket zero values.
    const customLegendsIds =
      seriesConfig.mode === 'stacked' && preserveSeriesOrder ? [...seriesConfig.seriesKeys] : undefined;

    // ── Resolve legends props ──

    const legendsProps = resolveLegendProps(legends, seriesTotals);

    // ── Resolve subtitle ──

    const resolvedSubtitle = header?.subtitle ?? filterHintText;

    return (
      <Chart
        isLoading={isLoading}
        colors={colors}
        labels={labels}
        strokeColors={strokeColors}
        style={style ?? { marginTop: theme.spacing(3) }}
        emptyStateConfig={{
          description: isLoading ? '' : t('No data is available for the selected criteria.'),
        }}
      >
        <Chart.HeaderV2
          subTitle={
            resolvedSubtitle ? (
              <Truncated color='subdued' as={Text} size='medium' css={{ maxWidth: '700px' }}>
                {resolvedSubtitle}
              </Truncated>
            ) : undefined
          }
          title={
            header?.title ? (
              <Chart.HeaderV2.Title
                infoTip={
                  header.infoTipDescription ? (
                    <AnalyticsInfoPopover description={header.infoTipDescription} />
                  ) : undefined
                }
              >
                {header.title}
              </Chart.HeaderV2.Title>
            ) : undefined
          }
          action={
            yAxis ? (
              <ChartEditPanel
                xAxisOptions={xAxis.options ?? X_AXIS_TIME_FILTER_OPTIONS}
                defaultXAxisSelected={xAxis.defaultSelected}
                onXChange={handleXChange}
                yAxisGroups={yAxis.groups}
                defaultYAxisSelected={yAxis.defaultSelected}
                onYChange={handleYChange}
              />
            ) : (
              <ChartEditPanel
                xAxisOptions={xAxis.options ?? X_AXIS_TIME_FILTER_OPTIONS}
                defaultXAxisSelected={xAxis.defaultSelected}
                onXChange={handleXChange}
              />
            )
          }
        />
        <Chart.BarChart
          appearance={
            {
              collectiveTooltip: true,
              customTooltipTitle: ({ groupName }: { groupName: string }) =>
                tooltipHeadersByName[groupName] ?? tooltipTitlesByName[groupName] ?? groupName,
              customXAxisTick: ({ groupName, ...rest }: { groupName: string; [key: string]: unknown }) => (
                <XAxisLabelValueTick {...rest} label={transformGroupName(groupName, xAxis.selected)} />
              ),
              height: chartHeight,
              showXAxis: true,
              showYAxis: true,
              ...appearance,
            } as BarChartAppearance
          }
          customLegendsIds={customLegendsIds}
          data={barChartData}
          onClick={xAxis.selected === TimeFilterType.DAILY && onBarClick ? handleBarClick : undefined}
        />
        <Chart.Legends {...legendsProps} />
      </Chart>
    );
  }
);

TimeBasedBarChart.displayName = 'TimeBasedBarChart';

// ── Helpers ──

/**
 * Resolves the `Chart.Legends` props from the consumer-facing `legends` config.
 *
 * Centralises the coupling rules (e.g. `allowClickOnCustomLegends` is only
 * forwarded when `customData` is present) so the JSX stays declarative.
 */
type ResolvedLegendProps = {
  values: Record<string, number>;
  formatValue: FormatValue;
  customData?: CustomLegendsData;
  allowClickOnCustomLegends?: boolean;
  doNotCollapseLegends?: boolean;
};

function resolveLegendProps(
  legends: TimeBasedBarChartProps['legends'],
  values: Record<string, number>
): ResolvedLegendProps {
  const props: ResolvedLegendProps = {
    values,
    formatValue: legends?.formatValue ?? formatters.value,
  };

  if (legends?.customData) {
    props.customData = legends.customData;

    if (legends.allowClickOnCustomLegends) {
      props.allowClickOnCustomLegends = true;
    }
  }

  if (legends?.doNotCollapseLegends) {
    props.doNotCollapseLegends = true;
  }

  return props;
}
